// "How it works" — Record / Review / Understand. Same pin mechanic as
// js/device-render.js: CSS position:sticky handles the visual pin, GSAP
// ScrollTrigger scrubs progress (0→1) across the tall track. Progress is
// split into three equal thirds — one per step — and the current third
// toggles a .is-active class on that step's text and photo; CSS owns the
// actual crossfade via transition, this only decides which index is
// current.
//
// Auto-advance is driven by the progress bar's own CSS animation
// completing (`animationend`), not a second timer running in parallel.
// An earlier version paired the CSS fill animation with a separate
// setTimeout of the same nominal duration — two independent clocks that
// only *look* synced. In practice they started at different moments
// (the CSS animation begins the instant .is-active is in the DOM, even
// before the section has scrolled into view; the JS timer only started
// once a scroll event reached it) and drifted apart, so the bar could
// visibly finish filling well before the timer fired, or the reverse.
// Driving the advance off the bar's own animationend means the visual
// and the behaviour are, by construction, the same event.
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

function initHowItWorks() {
  const track = document.getElementById("how-it-works");
  if (!track) return;

  const steps = Array.from(track.querySelectorAll(".how-it-works__step"));
  const images = Array.from(track.querySelectorAll(".how-it-works__img"));
  if (!steps.length || !images.length) return;

  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  // CSS fallback (prefers-reduced-motion block in styles.css) shows every
  // step's text at once and only the first photo, statically — no pin,
  // no bar, nothing here for it to drive.
  if (reducedMotion) return;

  // FIXED 2026-08-28, per Martina ("not mobile friendly whatsoever"):
  // the pin+crossfade technique assumes the side-by-side desktop layout,
  // which comfortably fits one viewport — the (now text-first, see
  // styles.css) mobile stack of photo + three headings, each with space
  // reserved for its body copy, doesn't, and the sticky stage's fixed
  // 100vh + overflow:hidden was silently clipping both ends of it. Same
  // breakpoint cards-scroll.js already uses for the identical reason
  // (see that file's own mobileQuery) — below it, skip the *pin* only.
  //
  // FOLLOW-UP 2026-08-28: an earlier version of this fix also dropped the
  // rail/timer/crossfade behavior entirely (static "show everything, one
  // photo" fallback, matching prefers-reduced-motion). Martina asked to
  // keep that behavior — she just doesn't want it tied to scroll
  // position on mobile, since nothing here can meaningfully scrub a
  // pinned crossfade on a phone (mobile's dynamic viewport height alone
  // makes 100vh sticky pins unreliable, before even considering that
  // "scroll to crossfade" isn't a gesture phone users reach for). So
  // mobile keeps setActive/restartAndArm below exactly as-is — same
  // rail fill, same 9s timing, same photo crossfade — just self-advances
  // in a loop (see mobileAdvance below) instead of scrolling the page.
  // Captured once at load, not resize-reactive — matches
  // js/device-render.js's own isMobile, and a page load is the only time
  // this realistically matters.
  // WIDENED 2026-08-28 (second follow-up), per Martina: was 767px — the
  // unpinned self-advancing mechanism should carry through tablet too;
  // 950px is the site's own shared mobile/desktop line for this pass
  // (matches cards-scroll.js, breath-score.js's isPhone, and
  // device-render.js's own isMobile).
  const mobileQuery = window.matchMedia("(max-width: 950px)");
  const mobileMode = mobileQuery.matches;

  // Horizon's own base.css makes .page-wrapper — not window — the real
  // scrolling element at >=990px (html/body get overflow:hidden there,
  // see assets/base.css); ScrollTrigger has to be told that explicitly
  // or it measures against a container that never scrolls, which is why
  // this section silently did nothing on desktop while working fine on
  // mobile/tablet. Below 990px window is still the real scroller,
  // matching Horizon's own breakpoint exactly. Captured once here, same
  // as mobileMode above — this file doesn't rebuild its trigger on resize.
  const desktopScroller = window.matchMedia("(min-width: 990px)").matches
    ? ".page-wrapper"
    : undefined;

  // Same reason as desktopScroller above: window.scrollTo() is a no-op
  // once .page-wrapper (not window) is the thing that actually scrolls
  // at >=990px — the auto-advance/click handlers below were computing a
  // correct target scroll position (trigger.start/end are already
  // relative to whichever scroller ScrollTrigger was told about) and then
  // asking the wrong element to move to it, so clicking a step or letting
  // one auto-advance silently did nothing on desktop.
  function scrollToY(y) {
    const scrollEl = desktopScroller
      ? document.querySelector(desktopScroller)
      : window;
    scrollEl.scrollTo({ top: y, behavior: "smooth" });
  }

  const count = steps.length;
  let current = -1; // sentinel: nothing armed yet, forces setActive(0) below to run for real
  let trigger = null; // desktop/tablet only — stays null in mobileMode

  // Restarts the given step's fill animation from empty (toggling the
  // inline style off/forcing a reflow/back on — re-adding the same CSS
  // class doesn't restart an already-finished animation) and arms a
  // fresh animationend listener for it. Any previous listener on this
  // element is removed first so repeated visits to the same step don't
  // stack handlers.
  function restartAndArm(index) {
    const fill = steps[index]?.querySelector(".how-it-works__bar-fill");
    if (!fill) return;

    if (fill._advanceHandler) {
      fill.removeEventListener("animationend", fill._advanceHandler);
    }

    fill.style.animation = "none";
    void fill.offsetWidth; // force reflow
    fill.style.animation = "";

    const handler = () => {
      fill.removeEventListener("animationend", handler);
      fill._advanceHandler = null;
      // Still the step showing — not advanced away from since the
      // animation started (shouldn't happen without user input in
      // either mode, but cheap to guard).
      if (current !== index) return;

      if (mobileMode) {
        // No scroll position to derive progress from — just keep
        // cycling Record → Review → Share → Record → ... for as long as
        // the section is on the page. Nothing pauses this when the
        // section scrolls out of view; that's an accepted trade-off for
        // "ambient" behavior instead of adding an IntersectionObserver
        // just to pause/resume it.
        setActive((index + 1) % count);
        return;
      }

      // Desktop/tablet: unchanged from before this follow-up. Guards:
      // this isn't the last step (nothing to advance to), and the
      // section is still pinned.
      if (index >= count - 1) return;
      if (!trigger || !trigger.isActive) return;
      // Middle of the next third, not its opening edge — landing exactly
      // on a boundary is one rounded pixel away from computing back to
      // the *current* index once the scroll settles (native smooth-scroll
      // easing, GSAP's own scrub catch-up, or a late ScrollTrigger
      // refresh from something else on the page loading mid-scroll can
      // all nudge the final position by a pixel or two), which silently
      // no-ops the advance. The middle of the next step has a full
      // third's worth of margin either side, so none of that matters.
      const nextMid = (index + 1.5) / count;
      const targetY = Math.round(
        trigger.start + nextMid * (trigger.end - trigger.start)
      );
      scrollToY(targetY);
    };
    fill._advanceHandler = handler;
    fill.addEventListener("animationend", handler);
  }

  function setActive(index) {
    if (index === current) return;
    current = index;
    steps.forEach((el, i) => el.classList.toggle("is-active", i === index));
    images.forEach((el, i) => el.classList.toggle("is-active", i === index));
    restartAndArm(index);
  }

  // ADDED 2026-08-28, per Martina: clicking a step should restart its
  // read timer, including clicking the step that's *already* active
  // (the exact case she described — didn't finish reading before it
  // auto-advanced, wants to jump back and get a fresh 9s rather than
  // watch it immediately advance again). setActive's own `index ===
  // current` guard exists to stop scroll-driven onUpdate from redoing
  // work on every scroll pixel within the same third — necessary there,
  // wrong here, since a click is a one-off, deliberate "restart this"
  // signal. Resetting the sentinel first forces setActive through even
  // when the index hasn't changed, without duplicating its class-toggle
  // + restartAndArm body.
  function forceActivate(index) {
    current = -1;
    setActive(index);
  }

  function onStepClick(index) {
    if (mobileMode) {
      forceActivate(index);
      return;
    }
    // Desktop/tablet: clicking the step already showing has no scroll
    // distance to travel, so nothing would fire onUpdate to restart it —
    // handle that case directly. Clicking a *different* step scrolls to
    // its own third instead of forcing the class toggle directly, so it
    // goes through the exact same onUpdate → setActive path scrolling
    // already uses (consistent with how auto-advance works, and avoids
    // a flash of this step's content before the smooth-scroll arrives).
    if (index === current) {
      forceActivate(index);
      return;
    }
    if (trigger) {
      const mid = (index + 0.5) / count; // middle of that step's own third, not the next one (contrast restartAndArm's nextMid above)
      const targetY = Math.round(
        trigger.start + mid * (trigger.end - trigger.start)
      );
      scrollToY(targetY);
    }
  }

  steps.forEach((step, i) => {
    step.addEventListener("click", () => onStepClick(i));
    // Keyboard parity for the same control — tabindex/role/label make it
    // a real focusable button rather than a click-only div, since this
    // is now an interactive control, not just decoration.
    step.tabIndex = 0;
    step.setAttribute("role", "button");
    step.setAttribute(
      "aria-label",
      `Show the "${step.querySelector(".how-it-works__heading")?.textContent ?? "step"}" step`
    );
    step.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onStepClick(i);
      }
    });
  });

  // Console-inspectable state, same idea as window.__debugDevice in
  // device-render.js — lets progress/isActive state be checked directly
  // without a screenshot.
  window.__debugHowItWorks = {
    trigger,
    get current() {
      return current;
    },
    steps,
    images,
    mobileMode,
  };

  if (mobileMode) {
    setActive(0); // starts the self-looping cycle via restartAndArm's animationend handler above
    return;
  }

  trigger = ScrollTrigger.create({
    trigger: track,
    start: "top top",
    end: "bottom bottom",
    scrub: 0.6,
    scroller: desktopScroller,
    onUpdate: (self) => {
      const index = Math.min(count - 1, Math.floor(self.progress * count));
      setActive(index);
    },
  });
  window.__debugHowItWorks.trigger = trigger;

  // Arm the first step immediately rather than waiting for a scroll
  // event to reach onUpdate — otherwise its bar (marked active in the
  // markup for no-JS/first-paint purposes) would already be sitting at
  // "finished" by the time anything is listening for its completion.
  setActive(0);

  // Every ScrollTrigger on the page (this one, cards-scroll, device-render)
  // gets its start/end positions calculated as soon as its module runs —
  // before the custom webfont (Italian Plate No2 Expanded, @font-face
  // font-display: swap) has finished loading. The swap from fallback to
  // custom face reflows heading/paragraph heights throughout the page,
  // shifting every section below the fold, and nothing was refreshing
  // ScrollTrigger afterward (device-render.js only refreshes on resize).
  // One global refresh once fonts settle fixes all of them, not just
  // this section's — ScrollTrigger.refresh() isn't scoped to the
  // instance that calls it.
  if (document.fonts?.ready) {
    document.fonts.ready.then(() => ScrollTrigger.refresh());
  }
}

initHowItWorks();
